import React, { useEffect, useState, useRef } from "react";
import {
  Drawer,
  Button,
  Spin,
  Tag,
  Typography,
  Divider,
  List,
  Avatar,
  Input,
  Form,
  Space,
  Tooltip,
  Modal,
} from "antd";
import {
  LoadingOutlined,
  SendOutlined,
  UserOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  ArrowDownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import TicketServices, {
  ITicket,
  TicketStatus,
  TicketPosterType,
} from "../../Services/TicketServices";
import { adminContext } from "../../Common/PageRoute";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ViewTicketProps {
  id: string | null;
  visible: boolean;
  onClose: (refresh?: boolean) => void;
}

const ViewTicket: React.FC<ViewTicketProps> = ({ id, visible, onClose }) => {
  const { openNotification } = React.useContext(adminContext);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState<ITicket | null>(null);
  const [reply, setReply] = useState("");
  const [deEscalateVisible, setDeEscalateVisible] = useState(false);
  const [deEscalateReason, setDeEscalateReason] = useState("");
  const [deEscalating, setDeEscalating] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (ticket?.thread) {
      scrollToBottom();
    }
  }, [ticket?.thread]);

  useEffect(() => {
    if (visible && id) {
      fetchTicket();
    } else {
      setTicket(null);
      setReply("");
      setShowReplyForm(false);
      setHasChanges(false);
    }
  }, [visible, id]);

  const fetchTicket = async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const res = await TicketServices.get(id!);
      if (res) {
        setTicket(res);
      } else {
        onClose();
      }
    } catch (error) {
      openNotification("Error", "An unexpected error occurred", "error");
      onClose();
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSubmitting(true);
    try {
      const res = await TicketServices.postReply(id!, reply);
      if (res && res.status) {
        openNotification("Success", "Reply posted successfully", "success");
        setReply("");
        setShowReplyForm(false);
        setHasChanges(true);
        await fetchTicket(true); // Silent refresh
      } else {
        openNotification(
          "Error",
          res?.message || "Failed to post reply",
          "error",
        );
      }
    } catch (error) {
      openNotification("Error", "An unexpected error occurred", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeEscalate = async () => {
    if (!deEscalateReason.trim() || deEscalateReason.length < 5) {
      openNotification(
        "Warning",
        "Please provide a reason (min 5 characters)",
        "warning",
      );
      return;
    }
    setDeEscalating(true);
    try {
      const res = await TicketServices.deEscalate(id!, deEscalateReason);
      if (res && res.status) {
        openNotification(
          "Success",
          "Ticket de-escalated successfully",
          "success",
        );
        setHasChanges(true);
        setDeEscalateVisible(false);
        setDeEscalateReason("");
        await fetchTicket(true); // Silent refresh
      } else {
        openNotification(
          "Error",
          res?.message || "Failed to de-escalate ticket",
          "error",
        );
      }
    } catch (error) {
      openNotification("Error", "An unexpected error occurred", "error");
    } finally {
      setDeEscalating(false);
    }
  };

  const getStatusTag = (status: TicketStatus) => {
    const map: any = {
      1: { color: "processing", text: "Open" },
      2: { color: "success", text: "Resolved" },
      3: { color: "default", text: "Closed" },
      7: { color: "blue", text: "In Progress" },
      10: { color: "red", text: "Escalated" },
    };
    const s = map[status] || { color: "default", text: "Unknown" };
    return <Tag color={s.color}>{s.text}</Tag>;
  };

  return (
    <Drawer
      title={ticket ? `Reference: ${ticket?.number}` : "Ticket Details"}
      placement="right"
      width={600}
      onClose={() => onClose(hasChanges)}
      open={visible}
      destroyOnClose
      closable={true}
      extra={
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => onClose(hasChanges)}
        />
      }
      footer={
        ticket &&
        ticket.status !== TicketStatus.Closed && (
          <div
            style={{
              padding: "10px 0px",
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <Button onClick={() => onClose(hasChanges)}>Cancel</Button>
            {!showReplyForm ? (
              <div style={{ textAlign: "right" }}>
                <Button type="primary" onClick={() => setShowReplyForm(true)}>
                  Reply
                </Button>
              </div>
            ) : (
              <>
                <TextArea
                  rows={4}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply here..."
                  disabled={submitting}
                  autoFocus
                />
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  <Button
                    onClick={() => {
                      setShowReplyForm(false);
                      setReply("");
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={submitting}
                    onClick={handleReply}
                    disabled={!reply.trim()}
                  >
                    Post Reply
                  </Button>
                </div>
              </>
            )}
          </div>
        )
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          <div style={{ marginTop: 16 }}>Loading ticket details...</div>
        </div>
      ) : ticket ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "12px 24px",
          }}
        >
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                {ticket.title}
              </Title>
              <Space>
                {ticket.status === TicketStatus.Escalated && (
                  <Button
                    size="small"
                    icon={<ArrowDownOutlined />}
                    onClick={() => setDeEscalateVisible(true)}
                  >
                    De-escalate
                  </Button>
                )}
                {getStatusTag(ticket.status)}
              </Space>
            </div>
            <Space split={<Divider type="vertical" />}>
              <Text type="secondary">
                <UserOutlined /> {ticket.createdBy?.userName}
              </Text>
              <Text type="secondary">
                <ClockCircleOutlined />{" "}
                {dayjs(ticket.createdBy?.date).format("DD/MM/YYYY HH:mm")}
              </Text>
            </Space>
            {ticket.company?.name && (
              <Text strong>Business: {ticket.company.name}</Text>
            )}
          </Space>

          <Divider />

          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: 16,
              borderRadius: 8,
              marginBottom: 24,
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: ticket.message }} />
          </div>

          {/* <Title level={5}>Thread</Title> */}
          {ticket.thread && ticket.thread.length > 0 && (
            <List
              itemLayout="horizontal"
              dataSource={ticket.thread || []}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<UserOutlined />}
                        style={{
                          backgroundColor:
                            item.poster?.type === TicketPosterType.Client
                              ? "#1890ff"
                              : "#87d068",
                        }}
                      />
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text strong>
                          {item.poster?.name}
                          <Text
                            type="secondary"
                            style={{ marginLeft: 8, fontWeight: "normal" }}
                          >
                            (
                            {item.poster?.type === TicketPosterType.Client
                              ? "Client"
                              : "Staff"}
                            )
                          </Text>
                        </Text>
                        <Text type="secondary">
                          {dayjs(item.createdAt).format("DD/MM/YYYY HH:mm")}
                        </Text>
                      </div>
                    }
                    description={
                      <div
                        style={{
                          marginTop: 8,
                          color: "#333",
                          padding: "8px 12px",
                          backgroundColor: item.isSystem
                            ? "#fffbe6"
                            : item.poster?.type === TicketPosterType.Client
                              ? "#e6f7ff"
                              : "#f6ffed",
                          borderRadius: 6,
                          border: item.isSystem ? "1px solid #ffe58f" : "none",
                        }}
                      >
                        {item.isSystem && (
                          <Tooltip title="System Message">
                            <InfoCircleOutlined
                              style={{ marginRight: 8, color: "#faad14" }}
                            />
                          </Tooltip>
                        )}
                        <div dangerouslySetInnerHTML={{ __html: item.body }} />
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
          <div ref={threadEndRef} />
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Text type="secondary">No ticket selected</Text>
        </div>
      )}

      <Modal
        title="De-escalate Ticket"
        open={deEscalateVisible}
        onOk={handleDeEscalate}
        onCancel={() => {
          setDeEscalateVisible(false);
          setDeEscalateReason("");
        }}
        confirmLoading={deEscalating}
        okText="Confirm De-escalation"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            Are you sure you want to de-escalate this ticket? This will move it
            back to Level 1 (In Progress).
          </Text>
        </div>
        <TextArea
          rows={3}
          value={deEscalateReason}
          onChange={(e) => setDeEscalateReason(e.target.value)}
          placeholder="Reason for de-escalation (required)..."
          disabled={deEscalating}
        />
      </Modal>
    </Drawer>
  );
};

export default ViewTicket;
